<template>

<div class="legal-overlay" v-on:click.self="$emit('close')">

	<div class="legal" role="dialog" aria-modal="true" :aria-label="content.title">

		<button class="legal-close" type="button" v-on:click="$emit('close')" aria-label="Close">&times;</button>

		<div class="legal-head">
			<h3 class="legal-title">{{ content.title }}</h3>
			<p class="legal-updated" v-if="content.updated">{{ content.updated }}</p>
		</div>

		<div class="legal-body">

			<p class="legal-summary" v-if="content.summary">{{ content.summary }}</p>

			<section class="legal-section" v-for="(sec, si) in content.sections" :key="si">

				<h4 class="legal-heading" v-if="sec.heading">{{ sec.heading }}</h4>

				<template v-for="(block, bi) in sec.blocks">
					<p class="legal-para" v-if="block.type === 'p'" :key="bi">{{ block.text }}</p>
					<ul class="legal-list" v-else :key="bi">
						<li v-for="(item, ii) in block.items" :key="ii">{{ item }}</li>
					</ul>
				</template>

			</section>

			<!-- About carries a way to back the project / get in touch -->
			<div class="legal-actions" v-if="page === 'about'">
				<a class="legal-btn is-patreon" href="https://www.patreon.com/geekgroup" target="_blank" rel="noopener">Patreon</a>
				<a class="legal-btn is-email" href="mailto:phil@geekgroup.app">Email me</a>
			</div>

		</div>

	</div>

</div>

</template>

<script>

/*
	The About / Privacy / Terms modal: predefined, plain-language content keyed
	by `page`, in whatboxart's editorial style. Self-contained overlay (boxart
	has no shared dialog). Triggered from the footer links. Adapted from Tally's
	reviewed policies, with the wording updated for whatboxart.
*/

var PAGES = {

	"about": {
		"title": "About whatboxart?",
		"sections": [
			{
				"heading": "",
				"blocks": [
					{ "type": "p", "text": "whatboxart? is a daily board game guessing game. A single game's box art is hidden behind a grid of sixteen tiles, revealed one at a time as you guess. Name the game from its cover in the fewest tiles, then share your result." },
					{ "type": "p", "text": "whatboxart was developed by Phil C, built on the geekgroup.app system. All game art and data comes from BoardGameGeek." }
				]
			}
		]
	},

	"privacy": {
		"updated": "Last updated: 22 June 2026",
		"summary": "whatboxart? is a daily board game guessing game. You can play without an account - we keep your guesses, result and score for each day. Sign in only if you want to keep your stats and streaks across devices. We don't sell your data, show ads, or track you for advertising.",
		"sections": [
			{
				"heading": "",
				"blocks": [
					{ "type": "p", "text": "This policy explains what information whatboxart collects, why, and what we do with it. We've tried to keep it plain. whatboxart is built by geekgroup and lives at boxart.geekgroup.app." },
					{ "type": "p", "text": "If you have any questions, email phil@geekgroup.app." }
				]
			},
			{
				"heading": "Who runs whatboxart",
				"blocks": [
					{ "type": "p", "text": "whatboxart is operated by geekgroup, a board-game collection website developed in the United Kingdom. geekgroup is the data controller responsible for your information." },
					{ "type": "ul", "items": [ "Contact: phil@geekgroup.app" ] },
					{ "type": "p", "text": "If UK GDPR or EU GDPR applies to you, geekgroup is the controller responsible for your information." }
				]
			},
			{
				"heading": "What whatboxart does",
				"blocks": [
					{ "type": "p", "text": "Each day there is one puzzle: a board game's box art hidden behind a 4x4 grid of tiles. You have sixteen guesses; the first tile is shown at the start, and each wrong guess reveals another tile, with the occasional hint along the way. Solve it in as few tiles as you can. An archive lets you replay past days for practice." }
				]
			},
			{
				"heading": "Playing doesn't need an account",
				"blocks": [
					{ "type": "p", "text": "You can play without signing in or giving us any personal details. For each day you play, we store, in aggregate, the outcome so the community results and leaderboards work:" },
					{ "type": "ul", "items": [
						"the guesses made and the result (solved or not)",
						"how many tiles were revealed and the score",
						"the display name you choose when you sign in (guests are anonymous)"
					] },
					{ "type": "p", "text": "To remember your progress on a day and your local stats, your browser saves a random token and your play history in local storage. This token is not linked to your real identity - it just tells us \"this is the same device that played before.\"" }
				]
			},
			{
				"heading": "Signing in (optional)",
				"blocks": [
					{ "type": "p", "text": "You only need to sign in if you want to keep your stats and streaks across devices and appear on the leaderboards. You can sign in using:" },
					{ "type": "ul", "items": [
						"Sign in with geekgroup (single sign-on with your geekgroup account)",
						"Google sign-in",
						"Email (we send a one-time sign-in link or code to your address)"
					] },
					{ "type": "p", "text": "For a signed-in account we store the identity your chosen provider gives us - an ID, and the name, email address and avatar you provide - along with your results, scores and streaks." },
					{ "type": "p", "text": "We use your email address only to send you the sign-in link or code. We don't use it for marketing." }
				]
			},
			{
				"heading": "Game data from BoardGameGeek",
				"blocks": [
					{ "type": "p", "text": "The box art, game names and details shown in whatboxart come from BoardGameGeek (BGG), via geekgroup's own database. We never ask for a BoardGameGeek password. Game names, details and artwork remain the property of BoardGameGeek and its contributors." }
				]
			},
			{
				"heading": "What we don't do",
				"blocks": [
					{ "type": "ul", "items": [
						"We do not sell your data.",
						"We do not show ads.",
						"We do not use your data for advertising or cross-site tracking."
					] }
				]
			},
			{
				"heading": "Analytics",
				"blocks": [
					{ "type": "p", "text": "We use Google Analytics to understand how whatboxart is used, for example how many people play and which screens they visit, so we can make it better. This is aggregate, usage-level information and relies on a cookie." },
					{ "type": "p", "text": "We have not turned on Google's advertising or cross-site features, so it isn't used to build an advertising profile of you. You can opt out with Google's browser add-on, or by blocking cookies for this site." }
				]
			},
			{
				"heading": "Cookies and local storage",
				"blocks": [
					{ "type": "p", "text": "whatboxart uses a small set of cookies and local storage: what it needs to run, plus Google Analytics to understand usage. There are no advertising cookies." },
					{ "type": "p", "text": "Cookies" },
					{ "type": "ul", "items": [
						"A session cookie to keep you signed in.",
						"A shared geekgroup.app cookie when you use geekgroup single sign-on.",
						"A Google Analytics cookie that helps us count visits and see how whatboxart is used."
					] },
					{ "type": "p", "text": "Local storage (kept in your own browser)" },
					{ "type": "ul", "items": [
						"An anonymous device token.",
						"Your progress and result for each day you play.",
						"Your local stats, so they show even before you sign in.",
						"Your sign-in session token, when signed in."
					] }
				]
			},
			{
				"heading": "Who else processes your data",
				"blocks": [
					{ "type": "p", "text": "We use a small number of trusted third parties (\"sub-processors\") to run whatboxart. Each handles only what it needs to:" },
					{ "type": "ul", "items": [
						"DigitalOcean - hosting and database.",
						"BoardGameGeek - source of game data and artwork.",
						"SparkPost - sends sign-in emails.",
						"Google - only if you choose to sign in with Google.",
						"Google Analytics - measures anonymous usage so we can improve whatboxart."
					] },
					{ "type": "p", "text": "Some of these providers are based outside the UK and EEA and may process data there. Where that happens, your data is protected by appropriate safeguards - such as Standard Contractual Clauses or an applicable adequacy decision - and you can read more in each provider's own privacy policy." }
				]
			},
			{
				"heading": "How long we keep things",
				"blocks": [
					{ "type": "p", "text": "Daily results and aggregate stats are kept so the leaderboards and community results stay meaningful." },
					{ "type": "p", "text": "If you have an account, or you want your data removed, you can ask us by emailing phil@geekgroup.app." }
				]
			},
			{
				"heading": "Your rights",
				"blocks": [
					{ "type": "p", "text": "If UK GDPR or EU GDPR applies to you, you have rights over your personal data, including the right to:" },
					{ "type": "ul", "items": [
						"access the data we hold about you",
						"correct inaccurate data",
						"ask us to delete your data",
						"object to or restrict certain processing",
						"request a copy of your data in a portable form",
						"withdraw consent where we relied on it"
					] },
					{ "type": "p", "text": "To exercise any of these, email phil@geekgroup.app. You also have the right to complain to a data protection authority. In the UK, that's the Information Commissioner's Office (ICO); in the EU, it's your local authority." }
				]
			},
			{
				"heading": "Legal bases (UK/EU GDPR)",
				"blocks": [
					{ "type": "p", "text": "Where GDPR applies, we rely on:" },
					{ "type": "ul", "items": [
						"Legitimate interests - to run the daily game and keep the service working.",
						"Consent - where you choose to sign in or link an account, and which you can withdraw at any time.",
						"Performance of our service - to provide the account features you've asked for."
					] }
				]
			},
			{
				"heading": "Children",
				"blocks": [
					{ "type": "p", "text": "whatboxart is a general-audience service. It is not directed at children under 13, and we don't knowingly collect their personal data." }
				]
			},
			{
				"heading": "Changes to this policy",
				"blocks": [
					{ "type": "p", "text": "We may update this policy from time to time. We'll change the \"last updated\" date above, and for significant changes we'll try to make the update clear in the app." }
				]
			}
		],
		"title": "Privacy Policy"
	},

	"terms": {
		"updated": "Last updated: 22 June 2026",
		"summary": "whatboxart? is a free daily board game guessing game. It's provided \"as is\" - please use it fairly and don't abuse it. We can change, pause or withdraw it at any time.",
		"sections": [
			{
				"heading": "",
				"blocks": [
					{ "type": "p", "text": "These terms cover your use of whatboxart at boxart.geekgroup.app. By using whatboxart, you agree to them. whatboxart is operated by geekgroup, based in the United Kingdom; you can reach us at phil@geekgroup.app." }
				]
			},
			{
				"heading": "What whatboxart is",
				"blocks": [
					{ "type": "p", "text": "whatboxart is a free daily game: guess the board game from its box art, revealed tile by tile. That's it - it's a small hobby product, offered to be useful and fun." }
				]
			},
			{
				"heading": "Using whatboxart fairly",
				"blocks": [
					{ "type": "p", "text": "When you use whatboxart, please:" },
					{ "type": "ul", "items": [
						"don't abuse or harass other people through it",
						"don't overload the service or try to disrupt it",
						"don't cheat or automate play to game the leaderboards"
					] },
					{ "type": "p", "text": "We may limit or block access if whatboxart is being misused." }
				]
			},
			{
				"heading": "Accounts",
				"blocks": [
					{ "type": "p", "text": "You don't need an account to play. If you do create one, keep your sign-in method secure. You're responsible for activity that happens through your account." }
				]
			},
			{
				"heading": "BoardGameGeek data",
				"blocks": [
					{ "type": "p", "text": "Game names, details and artwork shown in whatboxart come from BoardGameGeek and remain the property of BoardGameGeek and its contributors. whatboxart displays this data to run the game; it doesn't give you any rights over it." }
				]
			},
			{
				"heading": "The service is provided \"as is\"",
				"blocks": [
					{ "type": "p", "text": "whatboxart is free and provided \"as is\" and \"as available\", without warranties of any kind. We don't promise it will always be available, error-free, or that it will suit any particular purpose." }
				]
			},
			{
				"heading": "Changes to the service",
				"blocks": [
					{ "type": "p", "text": "We may change, pause or withdraw whatboxart - in whole or in part - at any time, including features like accounts or leaderboards. We'll try to avoid disruption where we reasonably can, but we can't guarantee continued availability." }
				]
			},
			{
				"heading": "Limitation of liability",
				"blocks": [
					{ "type": "p", "text": "To the fullest extent allowed by law, geekgroup is not liable for any loss or damage arising from your use of whatboxart, including lost data, lost time, or inability to use the service. Nothing in these terms limits any liability that can't be limited by law (for example, for death or personal injury caused by negligence, or for fraud)." }
				]
			},
			{
				"heading": "Changes to these terms",
				"blocks": [
					{ "type": "p", "text": "We may update these terms from time to time. We'll change the \"last updated\" date above, and for significant changes we'll try to make the update clear in the app. If you keep using whatboxart after a change, you're agreeing to the updated terms." }
				]
			},
			{
				"heading": "Governing law",
				"blocks": [
					{ "type": "p", "text": "These terms, and any dispute arising from them or from your use of whatboxart, are governed by the laws of England and Wales, and subject to the exclusive jurisdiction of the courts of England and Wales." }
				]
			},
			{
				"heading": "Contact",
				"blocks": [
					{ "type": "p", "text": "Questions about these terms? Email phil@geekgroup.app." }
				]
			}
		],
		"title": "Terms of Service"
	}

}

export default {

	props: {
		page: {
			default: 'about'
		}
	},

	computed: {

		content: function() {

			return PAGES[this.page] || PAGES.about

		}

	},

	created: function() {

		var self = this

		this.onKey = function(e) { if (e.key === 'Escape') self.$emit('close') }
		document.addEventListener('keydown', this.onKey)

	},

	beforeDestroy: function() {

		document.removeEventListener('keydown', this.onKey)

	}

}

</script>

<style scoped>

.legal-overlay {
	position: fixed;
	inset: 0;
	z-index: 300;
	background: rgba(28, 26, 22, 0.42);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px 16px;
	overflow-y: auto;
}

.legal {
	position: relative;
	margin: auto;
	width: 100%;
	max-width: 520px;
	max-height: calc(100vh - 48px);
	display: flex;
	flex-direction: column;
	background: var(--paper);
	border: 1px solid var(--hairline);
	border-radius: 14px;
	box-shadow: 0 24px 60px rgba(40, 34, 24, 0.28);
}

.legal-close {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 3;
	width: 44px;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	font-size: 28px;
	line-height: 1;
	color: var(--muted-2);
	cursor: pointer;
	border-radius: 8px;
	transition: background 140ms ease, color 140ms ease;
}

.legal-close:hover {
	color: var(--ink);
	background: #f4eee2;
}

.legal-head {
	padding: 22px 24px 14px;
	border-bottom: 1px solid var(--hairline);
	flex: none;
}

.legal-title {
	font-family: var(--font-display);
	font-size: 22px;
	font-weight: 600;
	margin: 0;
	color: var(--ink);
}

.legal-updated {
	margin: 4px 0 0;
	font-family: var(--font-mono);
	font-size: 11px;
	color: var(--muted);
}

.legal-body {
	padding: 18px 24px 24px;
	overflow-y: auto;
}

.legal-summary {
	font-family: var(--font-body);
	font-size: 14px;
	line-height: 1.55;
	color: var(--ink);
	background: #fbf8f1;
	border: 1px solid var(--hairline);
	border-radius: 10px;
	padding: 12px 14px;
	margin: 0 0 18px;
}

.legal-section {
	margin-bottom: 18px;
}

.legal-heading {
	font-family: var(--font-body);
	font-size: 15px;
	font-weight: 600;
	color: var(--ink);
	margin: 0 0 8px;
}

.legal-para {
	font-family: var(--font-body);
	font-size: 13.5px;
	line-height: 1.55;
	color: var(--muted-2);
	margin: 0 0 10px;
}

.legal-list {
	margin: 0 0 10px;
	padding-left: 20px;
	list-style: disc;
}

.legal-list li {
	font-family: var(--font-body);
	font-size: 13.5px;
	line-height: 1.5;
	color: var(--muted-2);
	margin-bottom: 6px;
}

.legal-list li::marker {
	color: var(--muted);
}

.legal-actions {
	display: flex;
	gap: 10px;
	margin-top: 6px;
}

.legal-btn {
	height: 42px;
	padding: 0 18px;
	display: inline-flex;
	align-items: center;
	border-radius: 8px;
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
}

.legal-btn.is-patreon {
	background: var(--ink);
	color: var(--paper);
}

.legal-btn.is-email {
	background: #fff;
	color: var(--ink);
	border: 1px solid var(--tile);
}

</style>
